import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  entityService,
  attributeService,
  attributeValueService,
  referenceService,
} from '../services';
import {
  Role,
  Asset,
  AssetName,
  AttributeSensitivity,
  formatData,
  handleError,
  getObjectNameFromSelection,
  resolveGlossaryAssetSubmit,
  fetchGlossaryAssetRows,
  fetchAttributeValues,
  searchAttributeValues,
  withValuePaths,
  canHaveAttributeValues,
  getAssetSubtitle,
  toFlatAssetRows,
  getStoredAssetListMode,
  ASSET_LIST_MODE,
  ASSET_LIST_MODE_KEY,
} from '../helpers';
import {
  Changelog,
  DataTable,
  Icon,
  MainColumn,
  ObjectDetail,
  ObjectDetails,
  References,
  Tabs,
  fetchToReferencesCount,
  PrincipalLink,
} from '../components';
import {
  useCustomProperties,
  useSideDialog,
  useModalDialog,
  useQueryParams,
  useGlobalState,
  usePrincipal,
  useNotFound,
  useMessage,
  useStatus,
} from '../hooks';

export const Entity = () => {
  const { queryParams } = useQueryParams();

  const [entity, setEntity] = useState([]);
  const [childEntities, setChildEntities] = useState([]);
  const [loggedInUser] = useGlobalState('loggedInUser');
  const [statuses] = useGlobalState('statuses');
  const [settings] = useGlobalState('settings');
  const [fromReferences, setFromReferences] = useState([]);
  const [toReferencesCount, setToReferencesCount] = useState({});
  const [selectedItems, setSelectedItems] = useState([]);
  const [enableEdit, setEnableEdit] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  const [defaultSelectedTab, setDefaultSelectedTab] = useState({ tab: queryParams.tab });
  // Tree or flat list, chosen in the toolbar and remembered between visits. Flat is the mode for
  // scanning: it lists every asset at one level, sorted by name.
  const [assetListMode, setAssetListMode] = useState(getStoredAssetListMode);
  // The values of the attributes that have been expanded, held apart from the tree rows so that
  // reloading the tree — which every save does — does not empty the open expansions. Kept flat, as
  // every other list of assets is, so the shared status decoration applies to it unchanged.
  const [attributeValues, setAttributeValues] = useState([]);

  const { glossary_id, entity_id } = useParams();

  const clearSelectedItems = () => {
    setSelectedItems([]);
  };

  const { showMessage } = useMessage();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog(clearSelectedItems);
  const { showModalDialog, hideModalDialog } = useModalDialog();
  const { fetchPrincipals } = usePrincipal();
  const { appendStatus } = useStatus({
    setState: setChildEntities,
    assetCategory: Asset.Glossary,
  });
  const { notFound, setNotFound, NotFound } = useNotFound();
  const { renderCustomPropertyValues, fetchCustomProperties } = useCustomProperties({
    objectName: Asset.Entity,
    objectIds: [entity_id],
  });

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchItems();
    fetchFromReferences();

    fetchToReferencesCount({ toObjectName: Asset.Entity, toObjectId: entity_id })
      .then(res => setToReferencesCount(res))
      .catch(err => {
        showMessage(err, 'error');
      });
  }, [entity_id]);

  // The child-entity listing re-runs when navigating to another entity and when the user toggles the
  // "Include Attributes" filter. Selection is cleared so the DataTable's "select all"
  // checkbox (which compares selected vs. visible row counts) stays consistent across reloads.
  useEffect(() => {
    fetchChildEntities();
    clearSelectedItems();
  }, [entity_id]);

  // Edit/Delete are only enabled when the current selection is of a single asset type — the
  // forms and delete flow operate on one type at a time.
  useEffect(() => {
    if (selectedItems.length > 0) {
      let tmpEnableEdit = true;
      let objectName = selectedItems[0]?.object_name;

      for (let i = 0; i < selectedItems.length; i++) {
        if (objectName !== selectedItems[i].object_name) {
          tmpEnableEdit = false;
          break;
        }
      }

      setEnableEdit(tmpEnableEdit);
    } else {
      setEnableEdit(true);
    }
  }, [selectedItems]);

  /**
   * Fetches the current entity and populates the main column.
   */
  const fetchItems = async () => {
    entityService
      .getById(entity_id)
      .then(res => {
        if (res.entities.length < 1) {
          setNotFound(true);
        } else {
          setEntity(res.entities[0]);
        }
      })
      .catch(err => {
        handleError({ err, showMessage, setNotFound });
      })
      .finally(res => setLoading(false));
  };

  /**
   * Fetches the assets this entity references (shown in the main column header).
   */
  const fetchFromReferences = async () => {
    referenceService
      .getByFromId(Asset.Entity, entity_id)
      .then(res => {
        setFromReferences(res.references);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /**
   * Returns the rows that form the subtree rooted at the current entity (all descendants,
   * excluding the entity itself), from a flat list of entities keyed by parent_entity_id.
   * The current entity's direct children are re-rooted (parent_unique_id set to null) so the
   * DataTable's hierarchy hook treats them as top-level rows — their real parent (the current entity)
   * is intentionally not in this list. Deeper descendants keep their parent_unique_id and nest under
   * those rows via the DataTable's parentIdColumn.
   * @param {Array<{ entity_id: number, parent_entity_id: number|null }>} rows
   * @returns {Array<object>}
   */
  const getDescendantEntities = rows => {
    const rootId = parseInt(entity_id);

    // Group rows by their parent id so the subtree can be walked without repeated scans.
    const childrenByParent = new Map();
    rows.forEach(row => {
      const key = row.parent_entity_id;
      if (!childrenByParent.has(key)) childrenByParent.set(key, []);
      childrenByParent.get(key).push(row);
    });

    // Walk the tree to collect the ids in the subtree (order here is irrelevant — it is a set).
    const descendantIds = new Set();
    const stack = [...(childrenByParent.get(rootId) || [])];
    while (stack.length) {
      const node = stack.pop();
      descendantIds.add(node.entity_id);
      const children = childrenByParent.get(node.entity_id);
      if (children) stack.push(...children);
    }

    // Build the result from the original rows so the backend's sort order is preserved, re-rooting
    // the current entity's direct children so they show as top-level rows.
    return rows
      .filter(row => descendantIds.has(row.entity_id))
      .map(row => (row.parent_entity_id === rootId ? { ...row, parent_unique_id: null } : row));
  };

  /**
   * Loads every entity in the glossary (optionally with attributes), decorates them
   * for the DataTable, and keeps only the subtree below the current entity for the "Entities"
   * tab. Also selects that tab by default whenever the current entity has descendants.
   */
  const fetchChildEntities = async () => {
    await fetchGlossaryAssetRows({ glossaryId: glossary_id })
      .then(({ rows: withPath, referenceCounts }) => {
        // The helper resolves each row's hierarchy path over the whole glossary, so a row's tooltip
        // shows its complete ancestry and not just the part visible inside this entity's subtree.

        // Scope to the current entity's subtree: keep its descendant entities, then keep the
        // attributes attached to the current entity or to any of those descendants. Attributes on the
        // current entity are re-rooted (parent_unique_id set to null) so they show as top-level rows
        // alongside the direct child entities, since the current entity itself is not in this list.
        const descendantEntities = getDescendantEntities(
          withPath.filter(x => x.object_name === Asset.Entity)
        );
        const currentUniqueId = `${Asset.Entity}-${parseInt(entity_id)}`;
        const parentUniqueIds = new Set([
          currentUniqueId,
          ...descendantEntities.map(x => x.unique_id),
        ]);
        const descendantAttributes = withPath
          .filter(x => x.object_name === Asset.Attribute && parentUniqueIds.has(x.parent_unique_id))
          .map(x =>
            x.parent_unique_id === currentUniqueId ? { ...x, parent_unique_id: null } : x
          );

        const combinedResult = [...descendantEntities, ...descendantAttributes];

        setChildEntities(combinedResult);
        appendStatus();
        setLoadingTable(false);
        updateReferenceCount(referenceCounts);
        fetchPrincipals(combinedResult, setChildEntities);

        // Open on the Entities tab when it has any rows (child entities or attributes),
        // otherwise fall back to Related Assets. An explicit ?tab= in the URL always wins.
        const hasChildren = combinedResult.length > 0;
        setDefaultSelectedTab({
          tab: queryParams.tab ? queryParams.tab : hasChildren ? 'assets' : 'related_assets',
        });
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /**
   * Merges the per-child "related assets" counts into the child rows and records the column maximum
   * used to scale the bar chart.
   * @param {Array<{ to_object_id: number, to_object_name: string, to_references_count: number }>} statistics
   */
  const updateReferenceCount = statistics => {
    let statistic = {};
    let maxValue = 0;

    statistics.forEach(row => {
      if (row.to_references_count > maxValue) {
        maxValue = parseInt(row.to_references_count);
      }
    });

    setChildEntities(prev =>
      prev.map(item => {
        statistic = statistics.find(
          x => x.to_object_id === item.object_id && x.to_object_name === item.object_name
        );
        return {
          ...item,
          to_references_count: statistic ? statistic.to_references_count : 0,
          to_references_count_max: maxValue,
        };
      })
    );
  };

  /**
   * Replaces the loaded values of the given attributes, leaving every other attribute's alone.
   * @param {Array<number|string>|number|string} attributeIds - The attributes just read
   * @param {object[]} values - Their values as just read, in one list
   */
  const mergeValues = (attributeIds, values) => {
    // A set of what is being replaced, so re-reading many attributes is one pass over the state and
    // one render rather than one of each per attribute.
    const ids = new Set([].concat(attributeIds).map(Number));

    setAttributeValues(prev => [
      ...prev.filter(value => !ids.has(Number(value.attribute_id))),
      ...values,
    ]);
    appendStatus({ setState: setAttributeValues, assetCategory: Asset.Glossary });
  };

  /**
   * Loads the values of one attribute, for expanding its row. The promise is returned so the table
   * can show the row as loading until they arrive.
   * @param {object} item - The row being expanded
   * @returns {Promise<void>|undefined} undefined for a row that has no values to load
   */
  const fetchValues = item => {
    if (item.object_name !== Asset.Attribute) return;

    // Already read once. The rows are still here — collapsing a row does not discard them — so
    // re-expanding shows them again without another request. Anything changed from this page re-reads
    // them (refreshValues), which is what keeps them current.
    if (attributeValues.some(value => Number(value.attribute_id) === Number(item.attribute_id))) {
      return;
    }

    return fetchAttributeValues({ attributeIds: [item.attribute_id] })
      .then(values => mergeValues(item.attribute_id, withValuePaths(values, childEntities)))
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /**
   * Re-reads the values of the attributes whose expansions are open, after one of them has changed.
   * The tree itself is reloaded separately, which is what refreshes the counts.
   *
   * All of them are read in one request: opening a row keeps its values until the page is left, so
   * what this covers grows as the tree is explored, and a request each would be a request per row
   * ever opened.
   *
   * @param {Array<number|string>} [attributeIds] - Attributes to re-read; defaults to every open one
   */
  const refreshValues = attributeIds => {
    const ids = attributeIds || [...new Set(attributeValues.map(value => value.attribute_id))];

    if (ids.length === 0) return Promise.resolve();

    return fetchAttributeValues({ attributeIds: ids })
      .then(values => mergeValues(ids, withValuePaths(values, childEntities)))
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /**
   * Searches the attribute values of this entity's subtree server-side, for the table's filter box.
   *
   * Scoped by the entities the table is showing rather than by the glossary: a value carries the
   * parent entity of its attribute, so this is exactly the set of attributes on screen — matches
   * under an attribute elsewhere in the glossary would have no row to appear under.
   *
   * @param {string} term - What the user typed
   * @param {{signal: AbortSignal, top: number, fields: string[]}} options - From the DataTable
   * @returns {Promise<object[]>} Matching value rows
   */
  const searchValues = (term, { signal, top, fields }) => {
    const entityIds = [
      parseInt(entity_id),
      ...childEntities.filter(row => row.object_name === Asset.Entity).map(row => row.entity_id),
    ];

    return searchAttributeValues({
      scope: `parent_entity_id in (${entityIds.join(',')})`,
      term,
      fields,
      top,
      signal,
    }).catch(err => {
      showMessage(err, 'error');
      return [];
    });
  };

  /**
   * Opens the correct "add" side dialog for the chosen asset type. Adding an entity defaults
   * to creating a child of the current entity; "Add Child" creates a child of the selected row.
   * @param {string} objectName - asset type or the 'AddChildEntity' action key
   */
  const handleAddAsset = objectName => {
    // The glossary asset form works with entities and attributes, and reads the selection to copy
    // from. A selected attribute value is neither, so it is left out and the form falls back to the
    // current entity rather than reading the value's own attribute as the asset being copied.
    const assetSelection = selectedItems.filter(item => item.object_name !== Asset.AttributeValue);

    switch (objectName) {
      case Asset.Entity:
        // Default "+ Add" creates a child of the entity currently being viewed.
        showSideDialog('addChildEntity', [entity], addAsset);
        break;
      case 'AddChildEntity':
        // "Add Child" creates a child of the single selected descendant entity.
        showSideDialog('addChildEntity', assetSelection, addAsset);
        break;
      case 'AddChildAttributeValue':
        // The selected row is the attribute the value belongs to, not something to copy, so only its
        // id is carried over — exactly as adding a value from the attribute list does.
        showSideDialog(
          'addAttributeValue',
          [
            {
              status_id: statuses.default_glossary_status_id,
              attribute_id: selectedItems[0].attribute_id,
            },
          ],
          addValue
        );
        break;
      case Asset.Attribute:
        // Seed the attribute from the selected row, or from the current entity when nothing
        // is selected, so its name and description are pre-filled.
        let items = assetSelection.length ? assetSelection : [entity];
        if (items.length === 1) {
          items[0] = {
            ...items[0],
            attribute_name: items[0].attribute_name || items[0].entity_name,
            attribute_description: items[0].attribute_description || items[0].entity_description,
          };
        }
        showSideDialog('addAttribute', items, addAsset);
        break;
      default:
      // nothing
    }
  };

  /**
   * Opens the edit side dialog for the selected rows, dispatching on their asset type.
   * @param {Array<object>} [items] - rows to edit; defaults to the current selection
   */
  const handleEditAsset = items => {
    items = items || selectedItems;

    const objectName = getObjectNameFromSelection(items);

    switch (objectName) {
      case Asset.Entity:
        showSideDialog('editEntity', items, editAsset);
        break;
      case Asset.Attribute:
        showSideDialog('editAttribute', items, editAsset);
        break;
      case Asset.AttributeValue:
        showSideDialog('editAttributeValue', items, editValue);
        break;
      case 'mixed':
        // Not supported, do nothing
        break;
      default:
      // nothing
    }
  };

  /**
   * Opens the delete confirmation for the selected rows, dispatching on their asset type.
   * @param {Array<object>} [items] - rows to delete; defaults to the current selection
   */
  const handleDeleteAsset = async items => {
    items = items || selectedItems;

    const objectName = getObjectNameFromSelection(items);

    switch (objectName) {
      case Asset.Entity:
        showModalDialog('deleteEntity', items, data => deleteAsset(data, Asset.Entity));
        break;
      case Asset.Attribute:
        showModalDialog('deleteAttribute', items, data => deleteAsset(data, Asset.Attribute));
        break;
      case Asset.AttributeValue:
        showModalDialog('deleteAttributeValue', items, data =>
          deleteAsset(data, Asset.AttributeValue)
        );
        break;
      case 'mixed':
        // Not supported, do nothing
        break;
      default:
      // nothing
    }
  };

  /**
   * Creates one or more child assets and refreshes the child listing.
   * @param {object|object[]} data - the asset(s) to create
   * @param {string} objectName - the asset type being created
   */
  const addAsset = async data => {
    // Which kind is being created comes from the role the user picked, not from the button they
    // pressed to open the form.
    const { service, payload } = resolveGlossaryAssetSubmit(data, 'add');

    try {
      const res = await service.create(payload).catch(err => {
        throw err;
      });

      setSideDialog(prev => ({ ...prev, formErrors: {} }));
      fetchChildEntities();
      showMessage(res.message);
    } catch (err) {
      handleError({ err, showMessage, setSideDialog });
      throw err;
    }
  };

  /**
   * Creates one or more attribute values under an attribute in the tree.
   * @param {object|object[]} data - The value(s) to create
   */
  const addValue = async data => {
    const rows = Array.isArray(data) ? data : [data];

    try {
      const res = await attributeValueService.create({ attribute_values: rows }).catch(err => {
        throw err;
      });

      setSideDialog(prev => ({ ...prev, formErrors: {} }));
      // The tree carries the value counts, so it is reloaded alongside the values themselves.
      fetchChildEntities();
      refreshValues([...new Set(rows.map(row => row.attribute_id))]);
      showMessage(res.message);
    } catch (err) {
      handleError({ err, showMessage, setSideDialog });
      throw err;
    }
  };

  /**
   * Updates one or more attribute values.
   * @param {object|object[]} data - The value(s) to update
   */
  const editValue = async data => {
    const rows = Array.isArray(data) ? data : [data];

    try {
      const res = await attributeValueService.update({ attribute_values: rows }).catch(err => {
        throw err;
      });

      // An edit can move a value to another attribute, so both the attribute it left and the one it
      // arrived at are re-read — which is every open expansion, plus any newly named attribute.
      refreshValues([
        ...new Set([
          ...attributeValues.map(value => value.attribute_id),
          ...rows.map(row => row.attribute_id),
        ]),
      ]);
      fetchChildEntities();
      showMessage(res.message);
      clearSelectedItems();
      hideSideDialog();
    } catch (err) {
      handleError({ err, showMessage, setSideDialog });
      throw err;
    }
  };

  /**
   * Updates one or more child assets and refreshes the child listing.
   * @param {object|object[]} data - the asset(s) to update
   * @param {string} objectName - the asset type being updated
   */
  const editAsset = async data => {
    // Changing the role across the Term/Dimension boundary moves the asset to the other resource, so
    // that arrives here as a create against the new kind rather than an update.
    const { service, payload, isCreate } = resolveGlossaryAssetSubmit(data, 'edit');

    try {
      const res = await (isCreate ? service.create(payload) : service.update(payload)).catch(
        err => {
          throw err;
        }
      );

      fetchChildEntities();
      // Saving an attribute can add, change and delete its values, which hang under it here as rows
      // of their own. The reload above refreshes the counts; the values themselves are held per
      // attribute and are re-read for the rows that are open.
      refreshValues();
      showMessage(res.message);
      clearSelectedItems();
      hideSideDialog();
    } catch (err) {
      handleError({ err, showMessage, setSideDialog });
      throw err;
    }
  };

  /**
   * Deletes one or more child assets and refreshes the child listing. For entities the
   * deleteChildren flag controls whether descendants are deleted or lifted up one level.
   * @param {object} data - delete payload ({ items, deleteChildren } for entities)
   * @param {string} objectName - the asset type being deleted
   */
  const deleteAsset = async (data, objectName) => {
    let service;

    switch (objectName) {
      case Asset.Entity:
        service = entityService;
        const items = Array.isArray(data.items) ? data.items : [data.items];
        data = { entities: items, deleteChildren: data.deleteChildren };
        break;
      case Asset.Attribute:
        service = attributeService;
        data = Array.isArray(data) ? data : [data];
        data = { attributes: data };
        break;
      case Asset.AttributeValue:
        service = attributeValueService;
        data = Array.isArray(data) ? data : [data];
        data = { attribute_values: data };
        break;
      default:
      // nothing
    }

    try {
      const res = await service.destroy(data).catch(err => {
        throw err;
      });

      hideModalDialog();
      clearSelectedItems();
      fetchChildEntities();
      // A deleted value leaves an open expansion showing it until its attribute is re-read.
      if (objectName === Asset.AttributeValue) refreshValues();
      showMessage(res.message);
    } catch (err) {
      showMessage(err, 'error');
    }
  };

  /**
   * Builds the "Add Child" button for the selected row: an asset under an entity, or a value under an
   * attribute. Only a Dimension can hold values, so for a Measure the button is shown but disabled
   * with the reason in its tooltip — the value form's own attribute picker offers only Dimensions,
   * but a pre-selected attribute never passes through it.
   * @returns {object} a DataTable button definition, or {} when the selection cannot have children
   */
  const getAddChildButton = () => {
    if (selectedItems.length !== 1) return {};

    if (selectedItems[0].object_name === Asset.Entity) {
      return {
        label: 'Add Child',
        tooltip: 'Add a new asset with the currently selected entity as parent',
        onClick: () => handleAddAsset('AddChildEntity'),
      };
    }

    if (selectedItems[0].object_name === Asset.Attribute) {
      const allowed = canHaveAttributeValues(selectedItems[0]);
      return {
        label: 'Add Child',
        disabled: !allowed,
        tooltip: allowed
          ? 'Add an attribute value to the currently selected attribute'
          : "Attribute Values can only be registered on Attributes with role 'Dimension'",
        onClick: () => handleAddAsset('AddChildAttributeValue'),
      };
    }

    return {};
  };

  // The tree: this entity's descendants and their attributes, plus the values of the attributes that
  // have been expanded. The values are rows of the same table, placed under their attribute by
  // parent_unique_id, which keeps their checkbox and their columns in line with the rows above them.
  const assetRows = useMemo(
    () => [...childEntities, ...attributeValues],
    [childEntities, attributeValues]
  );

  const isFlatAssetList = assetListMode === ASSET_LIST_MODE.flat;

  // What the table is given: the tree as it stands, or the same rows flattened.
  const assetTableRows = useMemo(
    () => (isFlatAssetList ? toFlatAssetRows(assetRows) : assetRows),
    [isFlatAssetList, assetRows]
  );

  /**
   * Updates the current entity itself (from the main column) and refreshes its details.
   *
   * Goes through the same resolver as the tree below, because the form's role picker can turn this
   * entity into an Attribute — which creates it in the other table rather than updating this one. Sending
   * the row to PUT /entity regardless failed on `target_object_name`, the form's marker for which kind
   * was chosen, since only the resolver strips it.
   *
   * @param {object|object[]} data - the entity to update
   */
  const editEntity = async data => {
    const { service, payload, isCreate, isConversion, targetObjectName } =
      resolveGlossaryAssetSubmit(data, 'edit');

    await (isCreate ? service.create(payload) : service.update(payload))
      .then(async res => {
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();

        // A conversion leaves nothing at this URL — the entity has been replaced by a row in the other
        // table — so follow the asset instead of re-reading an id that no longer exists.
        if (isConversion) {
          const converted = res.attributes?.[0] || res.entities?.[0];
          if (converted) {
            navigate(
              targetObjectName === Asset.Attribute
                ? `/attributes/${converted.attribute_id}`
                : `/glossaries/${converted.glossary_id}/${converted.entity_id}`
            );
            return;
          }
        }

        fetchItems();
        fetchCustomProperties(false);
        fetchFromReferences();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  /**
   * Deletes the current entity (from the main column) and navigates away.
   * @param {object} data - delete payload ({ items, deleteChildren })
   */
  const deleteEntity = async data => {
    await entityService
      // The delete modal supplies { items, deleteChildren }; the API expects entities to be
      // the array of entities with deleteChildren as a sibling flag.
      .destroy({ entities: data.items, deleteChildren: data.deleteChildren })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();

        if (location.key !== 'default') {
          // Arrived here by navigating inside Katalogue, so return the user where they came from.
          navigate(-1);
        } else {
          // Landed directly on this page — a deep link, a refresh or a new tab — so there is no
          // history to return to. Fall back to the deleted asset's parent.
          navigate(`/glossaries/${glossary_id}`);
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const data = loading ? [] : entity;

  const editable =
    loggedInUser &&
    (loggedInUser.user_role_name === Role.admin || loggedInUser.user_role_name === Role.editor);

  if (notFound) {
    return <NotFound />;
  }

  return (
    <div className="columns narrow-margin">
      <MainColumn>
        <ObjectDetails
          type={AssetName[Asset.Entity]}
          title={data.entity_name}
          statusId={data.status_id}
          subtitleIcon={<Icon name={'entity'} tooltip={'Entity'} />}
          subtitleText={data && !loading && getAssetSubtitle(data, Asset.Entity)}
          details={[
            ...renderCustomPropertyValues().details,
            { title: 'Last Modified', data: formatData(data.modified_timestamp, 'datetime') },
            { title: 'Created', data: formatData(data.created_timestamp, 'datetime') },
            {
              title: 'Id',
              data: data.entity_id,
              tooltip: 'Katalogue-internal id for this asset',
            },
          ]}
          description={data.entity_description}
          fromReferences={fromReferences}
          loading={loading}
          editable={editable}
          buttons={[
            {
              buttonStyle: 'main-button',
              value: 'Edit',
              onClick: () => showSideDialog('editEntity', [data], editEntity),
            },
            {
              value: 'Delete',
              onClick: () => showModalDialog('deleteEntity', [data], deleteEntity),
            },
          ]}
        >
          <ObjectDetail label="Owner" loading={loading}>
            <PrincipalLink principalId={data.owner_principal_id} loading={loading} />
          </ObjectDetail>

          {renderCustomPropertyValues().main}
        </ObjectDetails>
      </MainColumn>

      <div className="column">
        <Tabs
          className="slim left"
          //onTabChange={ onTabChange }
          defaultSelectedTab={defaultSelectedTab}
          disableTabsWithoutResults={false}
        >
          <div
            label="Assets"
            tabid="assets"
            resultCount={loadingTable ? undefined : childEntities.length}
          >
            <DataTable
              columns={[
                {
                  id: 'entity_name',
                  name: 'Name',
                  link: ':link',
                  tooltip: 'entity_description',
                  tooltipSubtitle: 'entity_path',
                  className: ':className',
                  // Every row carries its role — Entity, Dimension or Measure — and shows the same
                  // icon the asset form's role picker uses for it.
                  inline: [
                    { type: 'icon', icon: 'attribute_role_name' },
                    // A flat row has lost the nesting that said where it sits, so the assets above
                    // it are named on a second line under it. It reads as part of the name rather
                    // than as a property of the row, so it takes no header slot of its own
                    // (sortable: false) — the name's own sort orders these rows.
                    ...(isFlatAssetList
                      ? [
                          {
                            type: 'text',
                            value: 'entity_parent_path',
                            className: 'primary-column-text-block',
                            after: true,
                            block: true,
                            sortable: false,
                          },
                        ]
                      : []),
                  ],
                },
                {
                  id: 'entity_description',
                  name: 'Description',
                  type: 'description',
                  viewMode: 'descriptions',
                },
                {
                  id: 'entity_type_name',
                  name: 'Type',
                  viewMode: 'statistics',
                  className: 'small-text',
                  tooltip: 'entity_type_description',
                },
                {
                  id: 'to_references_count',
                  name: 'Related Assets',
                  type: 'chart',
                  // References to a value are not counted with the tree's, so the cell is left blank
                  // rather than reading "-", which would say the number is unknown.
                  appliesTo: [Asset.Entity, Asset.Attribute],
                  chartType: 'bar',
                  data: {
                    data: 'to_references_count',
                    max: 'to_references_count_max',
                  },
                  viewMode: 'statistics',
                },
                {
                  id: 'pii',
                  name: '',
                  type: 'icon',
                  tooltip: 'pii_tooltip',
                  className: 'no-right-margin',
                },
                {
                  id: 'attribute_sensitivity_name',
                  name: 'Data Sensitivity',
                  type: 'pill',
                  value: 'attribute_sensitivity_level',
                  values: AttributeSensitivity,
                  tooltip: 'attribute_sensitivity_name',
                },
                ...(settings.SHOW_GLOSSARY_STATUSES
                  ? [
                      {
                        id: 'status_name',
                        name: 'Status',
                        type: 'pill',
                        value: 'status_id',
                        values: statuses,
                        tooltip: 'status_description',
                      },
                    ]
                  : []),
                {
                  id: 'principal_name',
                  name: 'Owner',
                  type: 'owner',
                  // An owner belongs to the asset itself: a value inherits its parent's, and showing it
                  // again on every row would read as the value having an owner of its own.
                  appliesTo: [Asset.Entity, Asset.Attribute],
                  link: '/:principal_url',
                  data: { photo: 'principal_photo' },
                  tooltipSubtitle: 'principal_type_name',
                },
              ]}
              buttons={[
                {
                  label: '+ Add',
                  action: 'add',
                  mainButton: true,
                  // One button, because the form's role picker is what chooses between an entity and
                  // an attribute. It opens on Entity; the user can change the role there.
                  tooltip: 'Add a glossary asset',
                  onClick: () => handleAddAsset(Asset.Entity),
                },
                getAddChildButton(),
                {
                  label: 'Edit',
                  action: 'edit',
                  disabled: !enableEdit,
                  tooltip:
                    !enableEdit &&
                    'Assets of different types have been selected. Make sure to only select assets of the same type.',
                  onClick: items => handleEditAsset(items),
                },
                {
                  label: 'Delete',
                  action: 'delete',
                  disabled: !enableEdit,
                  tooltip:
                    !enableEdit &&
                    'Assets of different types have been selected. Make sure to only select assets of the same type.',
                  onClick: items => handleDeleteAsset(items),
                },
              ]}
              data={assetTableRows}
              idColumn="unique_id"
              primaryColumn="entity_name"
              parentIdColumn="parent_unique_id"
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              loading={loadingTable}
              editable={editable}
              filterable={true}
              filterObjects={[
                'entity_name',
                'entity_description',
                // Both kinds share this table, so the role — Entity, Dimension or Measure — and the
                // entity type are worth filtering on: "entity" alone, or the entities of one type.
                'attribute_role_name',
                'entity_type_name',
                'is_pii',
                'attribute_sensitivity_name',
                ...(settings.SHOW_GLOSSARY_STATUSES ? ['status_name'] : []),
                'principal_name',
              ]}
              filterControls={[
                {
                  type: 'select-buttons',
                  design: 'bright',
                  name: ASSET_LIST_MODE_KEY,
                  tooltip: 'Show the assets as a tree or as a flat list',
                  saveInLocalStorage: true,
                  defaultValue: assetListMode,
                  options: [
                    {
                      value: ASSET_LIST_MODE.tree,
                      icon: 'tree',
                      tooltip: 'Tree — show every asset under the asset it belongs to',
                    },
                    {
                      value: ASSET_LIST_MODE.flat,
                      icon: 'flat',
                      tooltip: 'Flat list — show every asset at one level, sorted by name',
                    },
                  ],
                  onChange: value => setAssetListMode(value[ASSET_LIST_MODE_KEY]),
                },
              ]}
              filterControlsPosition="right"
              // A flat list is sorted by name; the tree keeps the order it is built in, where a sort
              // could only order siblings within their parent.
              defaultSort={isFlatAssetList ? { key: 'entity_name', direction: 'asc' } : undefined}
              showViewModes={true}
              expandable={true}
              expandableIndentIcons={true}
              // An attribute's values are read when it is expanded, so the count is what tells the
              // table an attribute has any before they exist.
              childCountColumn="attribute_value_count"
              onExpand={fetchValues}
              childFilter={{ fetch: searchValues }}
            />
          </div>

          <div
            label="Related Assets"
            tabid="related_assets"
            resultCount={toReferencesCount && toReferencesCount.total}
          >
            <References
              showMessage={showMessage}
              toObjectName={Asset.Entity}
              toObjectId={entity_id}
              toReferencesCount={toReferencesCount}
            />
          </div>

          <div label="Changelog" tabid="changelog">
            <Changelog
              objectName={Asset.Entity}
              objectId={entity_id}
              filterObjects={[
                'changedData.new_data||old_data.entity_name',
                'changedData.new_data||old_data.entity_description',
                'changedData.new_data||old_data.glossary_name',
                'changedData.new_data||old_data.principal_name',
                'changedData.property',
                'changed_by_user_username',
                'is_service_account_user',
              ]}
              filterObjectsValueMap={{
                is_service_account_user: {
                  true: { label: 'Datasource Sync' },
                  false: { label: '' },
                },
              }}
            />
          </div>
        </Tabs>
      </div>
    </div>
  );
};
